import {Button, ModalCard} from "@vkontakte/vkui";
import {observer} from "mobx-react-lite";
import React from "react";
import {useRouteNavigator} from "@vkontakte/vk-mini-apps-router";
import {ReferralInviteCta} from "../../components/ReferralInviteCta/ReferralInviteCta";
import GameStore from "../../store/game";
import {
    MODAL_CARD_CLASS,
    MODAL_HEADER_CLASS,
    MODAL_INNER_CARD_CLASS,
    MODAL_PRIMARY_BUTTON_CLASS,
    MODAL_SECONDARY_BUTTON_CLASS,
    MODAL_SUBHEADER_CLASS
} from "../modalStyles";

export const GameResultModal = observer(({nav}) => {
    const routeNavigator = useRouteNavigator();
    const summary = GameStore.finishSummary || {};
    const foundWords = Array.isArray(GameStore.foundWords) ? GameStore.foundWords : [];
    const visibleWords = foundWords.slice(-20).reverse();
    const wordsCount = Number(summary.wordsCount ?? foundWords.length) || 0;
    const score = Number(summary.score ?? GameStore.score) || 0;
    const coinsReward = Number(summary.coinsReward ?? GameStore.earnedCoins) || 0;
    const progression = summary.progression || null;
    const missionDeltas = Array.isArray(summary.missionDeltas) ? summary.missionDeltas.slice(0, 2) : [];
    const tournamentDeltas = Array.isArray(summary.tournamentDeltas) ? summary.tournamentDeltas.slice(0, 1) : [];
    const newCollectionWords = Array.isArray(summary.newCollectionWords) ? summary.newCollectionWords.slice(0, 8) : [];
    const canReplay = Number(GameStore.dailyAttempts) > 0;
    const levelProgress = progression
        ? Math.min(100, Math.max(0, Math.round(((progression.totalScore - progression.currentLevelScore) / Math.max(progression.nextLevelScore - progression.currentLevelScore, 1)) * 100)))
        : 0;
    const titleText = summary.isNewBestScore ? "Новый рекорд" : "Раунд завершён";
    const subheaderText = wordsCount > 0
        ? "Каждый раунд двигает личный прогресс вперёд."
        : "Слова спрятались хорошо. Следующий заход может быть сильнее.";

    const stats = [
        {label: "Счёт", value: score, accent: "text-gray-900", hint: summary.isNewBestScore ? "Лучший результат" : "Очки за этот раунд"},
        {label: "Слова", value: wordsCount, accent: "text-gray-900", hint: "Найдено за игру"},
        {label: "Монеты", value: coinsReward, accent: "text-amber-700", hint: "Начислено за раунд"},
    ];

    const closeModal = () => routeNavigator.hideModal();
    const playAgain = () => {
        if (!canReplay) {
            return;
        }
        closeModal();
        GameStore.requestReplay();
    };
    const goHome = () => {
        closeModal();
        routeNavigator.replace("/");
    };

    return (
        <ModalCard
            nav={nav}
            onClose={closeModal}
            className={MODAL_CARD_CLASS}
            header={<span className={MODAL_HEADER_CLASS}>{titleText}</span>}
            subheader={<span className={MODAL_SUBHEADER_CLASS}>{subheaderText}</span>}
            actions={
                <div className="flex flex-col w-full gap-2">
                    <Button
                        size="l"
                        mode="primary"
                        className={MODAL_PRIMARY_BUTTON_CLASS}
                        onClick={playAgain}
                        disabled={!canReplay}
                    >
                        Играть ещё
                    </Button>
                    <Button
                        size="l"
                        mode="secondary"
                        className={MODAL_SECONDARY_BUTTON_CLASS}
                        onClick={goHome}
                    >
                        На главную
                    </Button>
                </div>
            }
        >
            <div className="flex flex-col w-full gap-3 mt-2">
                <div className="grid grid-cols-3 gap-2">
                    {stats.map(({label, value, accent, hint}) => (
                        <div key={label} className={`rounded-lg px-3 py-2 ${MODAL_INNER_CARD_CLASS}`}>
                            <div className="text-xs text-gray-700">{label}</div>
                            <div className={`text-xl font-semibold ${accent}`}>{value}</div>
                            <div className="text-[11px] text-gray-700">{hint}</div>
                        </div>
                    ))}
                </div>

                {progression ? (
                    <div className={`rounded-lg px-3 py-2 ${MODAL_INNER_CARD_CLASS}`}>
                        <div className="flex items-center justify-between gap-2">
                            <div>
                                <div className="text-xs text-gray-700">Уровень {progression.level}</div>
                                <div className="text-sm font-semibold text-gray-900">{progression.title}</div>
                            </div>
                            {progression.leveledUp ? (
                                <div className="text-xs font-semibold text-emerald-700">Повышение</div>
                            ) : null}
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-gray-300 overflow-hidden">
                            <div className="h-full rounded-full bg-emerald-500" style={{width: `${levelProgress}%`}}/>
                        </div>
                        <div className="mt-1 text-[11px] text-gray-700">
                            {progression.totalScore} / {progression.nextLevelScore} очков
                        </div>
                    </div>
                ) : null}

                {missionDeltas.length > 0 ? (
                    <div className={`rounded-lg px-3 py-2 ${MODAL_INNER_CARD_CLASS}`}>
                        <div className="text-xs text-gray-700 mb-2">Миссии</div>
                        <div className="flex flex-col gap-2">
                            {missionDeltas.map((mission) => (
                                <div key={mission.id} className="flex items-center justify-between gap-2 text-sm">
                                    <span className="text-gray-900">{mission.title}</span>
                                    <span className="text-gray-700">{mission.after}/{mission.targetValue}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}

                {tournamentDeltas.length > 0 ? (
                    <div className={`rounded-lg px-3 py-2 ${MODAL_INNER_CARD_CLASS}`}>
                        {tournamentDeltas.map((item) => (
                            <div key={item.tournamentId} className="text-sm text-gray-900">
                                Турнир: {item.position} место, {item.currentScore} очков
                            </div>
                        ))}
                    </div>
                ) : null}

                {newCollectionWords.length > 0 ? (
                    <div className={`rounded-lg px-3 py-2 ${MODAL_INNER_CARD_CLASS}`}>
                        <div className="text-xs text-gray-700 mb-2">Коллекция пополнена</div>
                        <div className="flex flex-wrap gap-1.5">
                            {newCollectionWords.map((item) => (
                                <span
                                    key={item.word}
                                    className="rounded-md bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-800"
                                >
                                    {item.word}
                                </span>
                            ))}
                        </div>
                    </div>
                ) : null}

                <div className={`rounded-lg px-3 py-2 ${MODAL_INNER_CARD_CLASS}`}>
                    <div className="text-xs text-gray-700 mb-2">Найденные слова</div>
                    {visibleWords.length === 0 ? (
                        <div className="text-sm text-gray-700">Пока нет найденных слов</div>
                    ) : (
                        <div className="flex flex-wrap gap-1.5">
                            {visibleWords.map((word, index) => (
                                <span
                                    key={`${word}-${index}`}
                                    className="rounded-md bg-gray-300 px-2 py-1 text-xs text-gray-800"
                                >
                                    {word}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                <ReferralInviteCta source="game_finished" variant="inline"/>
            </div>
        </ModalCard>
    );
});
